import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Rate limiting configuration
export interface RateLimitConfig {
  maxRequests: number; // Maximum requests allowed
  windowMs: number; // Time window in milliseconds
  retryAfterMs?: number; // Retry delay after rate limit hit
  backoffMultiplier?: number; // Exponential backoff multiplier
}

// Default rate limiting configurations
export const DEFAULT_RATE_LIMITS = {
  // General API calls
  default: { maxRequests: 100, windowMs: 60000 }, // 100 requests per minute

  // Search and filtering
  search: { maxRequests: 30, windowMs: 60000 }, // 30 searches per minute

  // Data mutations (POST, PUT, PATCH, DELETE)
  mutation: { maxRequests: 20, windowMs: 60000 }, // 20 mutations per minute

  // File uploads
  upload: { maxRequests: 10, windowMs: 60000 }, // 10 uploads per minute

  // Authentication
  auth: { maxRequests: 5, windowMs: 60000 }, // 5 auth attempts per minute

  // Real-time updates
  realtime: { maxRequests: 200, windowMs: 60000 }, // 200 updates per minute
} as const;

// Rate limiter class
class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  private blockedEndpoints: Map<string, number> = new Map();

  constructor(private config: RateLimitConfig) {}

  // Check if request is allowed
  canMakeRequest(endpoint: string): boolean {
    const now = Date.now();
    const windowStart = now - this.config.windowMs;

    // Check if endpoint is blocked
    const blockedUntil = this.blockedEndpoints.get(endpoint);
    if (blockedUntil && now < blockedUntil) {
      return false;
    }

    // Get request history for this endpoint
    const requestTimes = this.requests.get(endpoint) || [];

    // Remove old requests outside the window
    const recentRequests = requestTimes.filter((time) => time > windowStart);

    // Check if we're under the limit
    if (recentRequests.length < this.config.maxRequests) {
      // Add current request
      recentRequests.push(now);
      this.requests.set(endpoint, recentRequests);
      return true;
    }

    // Rate limit exceeded - block the endpoint
    const retryAfter = this.config.retryAfterMs || 5000; // Default 5 seconds
    this.blockedEndpoints.set(endpoint, now + retryAfter);

    return false;
  }

  // Get time until next allowed request
  getTimeUntilNextRequest(endpoint: string): number {
    const blockedUntil = this.blockedEndpoints.get(endpoint);
    if (!blockedUntil) return 0;

    const now = Date.now();
    return Math.max(0, blockedUntil - now);
  }

  // Reset rate limiter for an endpoint
  reset(endpoint: string): void {
    this.requests.delete(endpoint);
    this.blockedEndpoints.delete(endpoint);
  }

  // Reset all rate limiters
  resetAll(): void {
    this.requests.clear();
    this.blockedEndpoints.clear();
  }
}

// Global rate limiters
const rateLimiters = new Map<string, RateLimiter>();

// Initialize rate limiters
Object.entries(DEFAULT_RATE_LIMITS).forEach(([key, config]) => {
  rateLimiters.set(key, new RateLimiter(config));
});

// Rate limiting middleware for RTK Query
export const createRateLimitedBaseQuery = (
  baseQuery: ReturnType<typeof fetchBaseQuery>,
  rateLimitType: keyof typeof DEFAULT_RATE_LIMITS = "default"
) => {
  return async (args: any, api: any, extraOptions: any) => {
    const limiter = rateLimiters.get(rateLimitType);
    const endpoint = typeof args === "string" ? args : args.url;

    if (!limiter) {
      console.warn(`No rate limiter found for type: ${rateLimitType}`);
      return baseQuery(args, api, extraOptions);
    }

    // Check if request is allowed
    if (!limiter.canMakeRequest(endpoint)) {
      const retryAfter = limiter.getTimeUntilNextRequest(endpoint);

      return {
        error: {
          status: 429,
          statusText: "Too Many Requests",
          data: {
            code: "TooManyRequestsError",
            message: `Rate limit exceeded. Please wait ${Math.ceil(
              retryAfter / 1000
            )} seconds before trying again.`,
            retryAfter,
          },
        },
      };
    }

    // Make the actual request
    return baseQuery(args, api, extraOptions);
  };
};

// Debounced function with rate limiting
export const createRateLimitedDebounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number,
  rateLimitType: keyof typeof DEFAULT_RATE_LIMITS = "default"
): T => {
  let timeoutId: NodeJS.Timeout;
  const limiter = rateLimiters.get(rateLimitType);

  return ((...args: any[]) => {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      if (limiter && !limiter.canMakeRequest("debounced")) {
        console.warn("Rate limit exceeded for debounced function");
        return;
      }
      func(...args);
    }, delay);
  }) as T;
};

// Throttled function with rate limiting
export const createRateLimitedThrottle = <T extends (...args: any[]) => any>(
  func: T,
  delay: number,
  rateLimitType: keyof typeof DEFAULT_RATE_LIMITS = "default"
): T => {
  let lastCall = 0;
  const limiter = rateLimiters.get(rateLimitType);

  return ((...args: any[]) => {
    const now = Date.now();

    if (now - lastCall >= delay) {
      if (limiter && !limiter.canMakeRequest("throttled")) {
        console.warn("Rate limit exceeded for throttled function");
        return;
      }
      lastCall = now;
      func(...args);
    }
  }) as T;
};

// Exponential backoff retry with rate limiting
export const createRateLimitedRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000,
  rateLimitType: keyof typeof DEFAULT_RATE_LIMITS = "default"
): Promise<T> => {
  const limiter = rateLimiters.get(rateLimitType);

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (limiter && !limiter.canMakeRequest("retry")) {
        const retryAfter = limiter.getTimeUntilNextRequest("retry");
        await new Promise((resolve) => setTimeout(resolve, retryAfter));
      }

      return await operation();
    } catch (error: any) {
      if (attempt === maxRetries) {
        throw error;
      }

      // Check if it's a rate limit error
      if (error?.status === 429) {
        const retryAfter =
          error?.data?.retryAfter || baseDelay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, retryAfter));
        continue;
      }

      // For other errors, use exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw new Error("Max retries exceeded");
};

// Hook for rate limiting in components
export const useRateLimit = (
  rateLimitType: keyof typeof DEFAULT_RATE_LIMITS = "default"
) => {
  const limiter = rateLimiters.get(rateLimitType);

  const canMakeRequest = (endpoint: string) => {
    return limiter ? limiter.canMakeRequest(endpoint) : true;
  };

  const getTimeUntilNextRequest = (endpoint: string) => {
    return limiter ? limiter.getTimeUntilNextRequest(endpoint) : 0;
  };

  const reset = (endpoint?: string) => {
    if (limiter) {
      if (endpoint) {
        limiter.reset(endpoint);
      } else {
        limiter.resetAll();
      }
    }
  };

  return {
    canMakeRequest,
    getTimeUntilNextRequest,
    reset,
    config: limiter ? DEFAULT_RATE_LIMITS[rateLimitType] : null,
  };
};

// Utility to create rate-limited API endpoints
export const createRateLimitedEndpoint = (
  endpoint: any,
  rateLimitType: keyof typeof DEFAULT_RATE_LIMITS = "default"
) => {
  return {
    ...endpoint,
    async onQueryStarted(arg: any, { dispatch, queryFulfilled }: any) {
      const limiter = rateLimiters.get(rateLimitType);
      const endpointKey = typeof arg === "string" ? arg : JSON.stringify(arg);

      if (limiter && !limiter.canMakeRequest(endpointKey)) {
        throw new Error("Rate limit exceeded");
      }

      return endpoint.onQueryStarted?.(arg, { dispatch, queryFulfilled });
    },
  };
};

// Export rate limiters for direct access
export { rateLimiters };
